import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin, Video } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import Seo from "../components/Seo";

const AXES = [["skills", "Skills"], ["experience", "Experience"], ["communication", "Communication"], ["culture_fit", "Culture fit"], ["overall", "Overall"]];
const STATUS_LABELS = { awaiting_candidate: "Awaiting time selection", scheduled: "Confirmed", reschedule_requested: "Reschedule requested", awaiting_reconfirmation: "Awaiting reconfirmation", completed: "Completed", no_show: "No-show", declined: "Declined", cancelled: "Cancelled" };
const PILL_STYLES = { awaiting_candidate: "bg-amber-50 text-amber-700", scheduled: "bg-green-50 text-green-700", reschedule_requested: "bg-orange-50 text-orange-700", awaiting_reconfirmation: "bg-amber-50 text-amber-700", completed: "bg-blue-50 text-blue-700", no_show: "bg-red-50 text-red-700", declined: "bg-zinc-100 text-zinc-500", cancelled: "bg-zinc-100 text-zinc-400" };

export default function InterviewRoom() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [scores, setScores] = useState({ skills: 0, experience: 0, communication: 0, culture_fit: 0, overall: 0, comment: "" });
  const [saving, setSaving] = useState("");

  const load = useCallback(() => {
    api.get(`/interviews/${id}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.status === 401 ? "auth" : "missing"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err === "auth") {
    return (
      <main className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="ivroom-auth">
        <h1 className="font-display text-2xl font-bold text-zinc-950">Log in to view this interview</h1>
        <Link to={`/login?next=/interviews/${id}`} data-testid="ivroom-login"
          className="mt-6 inline-flex bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors">
          Log in
        </Link>
      </main>
    );
  }
  if (err || !data) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="ivroom-missing">
        <h1 className="font-display text-2xl font-bold text-zinc-950">Interview not found</h1>
        <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-950">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
      </main>
    );
  }

  const iv = data.interview;
  const isEmployer = data.viewer === "employer";

  const addNote = async (e) => {
    e.preventDefault();
    setSaving("note");
    try {
      await api.post(`/employer/interviews/${id}/notes`, { text: note });
      setNote("");
      load();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(""); }
  };

  const saveScores = async (e) => {
    e.preventDefault();
    setSaving("score");
    try {
      await api.post(`/employer/interviews/${id}/scorecard`, scores);
      toast.success("Scorecard saved.");
      load();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(""); }
  };

  const mark = async (attended) => {
    try {
      await api.post(`/employer/interviews/${id}/attendance`, { attended });
      toast.success(attended ? "Marked as completed." : "Marked as no-show.");
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="ivroom">
      <Seo title={`Interview · ${iv.job_title} | HiredToday`} description="Interview details on HiredToday." path={`/interviews/${id}`} noindex />
      <Link to="/dashboard" data-testid="ivroom-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-bold text-zinc-950">{iv.title || `Interview · ${iv.job_title}`}</h1>
            <p className="text-sm text-zinc-500 mt-1">{isEmployer ? `${iv.candidate_name} · ` : ""}{iv.job_title} · {iv.company_name}{iv.round > 1 ? ` · Round ${iv.round}` : ""}</p>
          </div>
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mt-1 ${PILL_STYLES[iv.status] || "bg-zinc-100 text-zinc-500"}`} data-testid="ivroom-status">
            {STATUS_LABELS[iv.status] || iv.status.replace(/_/g, " ")}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm text-zinc-600">
          {iv.when_str && <span className="inline-flex items-center gap-1.5" data-testid="ivroom-when"><Calendar className="h-4 w-4 text-zinc-400" />{iv.when_str}</span>}
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-zinc-400" />{iv.duration_minutes} min</span>
          <span className="inline-flex items-center gap-1.5"><Video className="h-4 w-4 text-zinc-400" />{iv.provider_label}</span>
          {iv.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-zinc-400" />{iv.location}</span>}
        </div>

        {iv.interviewers?.length > 0 && (
          <p className="mt-3 text-xs text-zinc-500" data-testid="ivroom-interviewers">
            {isEmployer ? "Interviewers" : "You will meet"}: {iv.interviewers.map((i) => i.name).join(", ")}
          </p>
        )}

        {iv.status === "scheduled" && iv.link && iv.link.startsWith("https://") && (
          <a href={iv.link} target="_blank" rel="noreferrer" data-testid="ivroom-join"
            className="mt-6 inline-flex items-center gap-2 h-12 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors">
            <Video className="h-4 w-4" /> Join interview
          </a>
        )}
        {iv.status === "scheduled" && iv.provider === "in_person" && iv.location && (
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-800" data-testid="ivroom-inperson">
            <MapPin className="h-4 w-4 text-zinc-500" /> In person at: {iv.location}
          </p>
        )}

        {!isEmployer && iv.status === "awaiting_reconfirmation" && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="ivroom-reconfirm">
            <p className="text-sm text-amber-900">The employer updated this interview. Please check the details above and reconfirm.</p>
            <button onClick={async () => { await api.post(`/candidate/interviews/${id}/reconfirm`); toast.success("Interview reconfirmed."); load(); }}
              data-testid="ivroom-reconfirm-yes"
              className="mt-3 h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors">Reconfirm interview</button>
          </div>
        )}

        {!isEmployer && ["scheduled", "awaiting_reconfirmation"].includes(iv.status) && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="ivroom-candidate-actions">
            <button
              onClick={async () => {
                const reason = window.prompt("Reason (optional):", "");
                if (reason === null) return;
                try {
                  await api.post(`/candidate/interviews/${id}/reschedule-request`, { reason });
                  toast.success("Reschedule requested, the employer has been notified.");
                  load();
                } catch (e2) { toast.error(formatApiError(e2)); }
              }}
              data-testid="ivroom-reschedule"
              className="h-9 px-4 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Request reschedule</button>
            <button
              onClick={async () => {
                if (!window.confirm("Decline this interview? The employer will be notified and reminders will stop.")) return;
                const reason = window.prompt("Reason (optional):", "") || "";
                try {
                  await api.post(`/candidate/interviews/${id}/decline`, { reason });
                  toast.success("Interview declined.");
                  load();
                } catch (e2) { toast.error(formatApiError(e2)); }
              }}
              data-testid="ivroom-decline"
              className="h-9 px-4 text-xs font-semibold border border-zinc-300 rounded-md hover:border-red-400 hover:text-red-600 transition-colors">Decline interview</button>
          </div>
        )}

        {!isEmployer && iv.status === "awaiting_candidate" && (
          <p className="mt-6 text-sm text-zinc-600" data-testid="ivroom-choose-hint">
            This interview is waiting on you to pick a time — choose one from your dashboard's Interviews tab.
          </p>
        )}

        {iv.recording?.requested && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900" data-testid="ivroom-recording">
            Recording requested by the employer · {iv.recording.consent === null ? "candidate has not answered yet" : iv.recording.consent ? "candidate consented" : "candidate declined, do not record"}
          </div>
        )}

        {!isEmployer && iv.recording?.requested && iv.recording?.consent === null && (
          <div className="flex gap-2 mt-3">
            <button onClick={async () => { await api.post(`/candidate/interviews/${id}/recording-consent`, { consent: true }); load(); }}
              data-testid="ivroom-consent-yes"
              className="h-9 px-4 text-xs font-semibold bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors">I consent to recording</button>
            <button onClick={async () => { await api.post(`/candidate/interviews/${id}/recording-consent`, { consent: false }); load(); }}
              data-testid="ivroom-consent-no"
              className="h-9 px-4 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">No thanks</button>
          </div>
        )}
      </div>

      {isEmployer && (
        <>
          {iv.kit && (
            <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-8 mt-6" data-testid="ivroom-kit">
              <h2 className="font-display text-lg font-bold text-zinc-950 mb-3">Interview kit · {iv.kit.name}</h2>
              <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700">
                {iv.kit.questions.map((q, i) => <li key={`${i}-${q.slice(0, 24)}`}>{q}</li>)}
              </ol>
            </div>
          )}

          <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-8 mt-6" data-testid="ivroom-scorecard">
            <h2 className="font-display text-lg font-bold text-zinc-950 mb-3">Scorecard</h2>
            <form onSubmit={saveScores} className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {AXES.map(([key, label]) => (
                  <label key={key} className="text-xs text-zinc-500">{label}
                    <select value={scores[key]} onChange={(e) => setScores({ ...scores, [key]: parseInt(e.target.value, 10) })}
                      data-testid={`ivroom-score-${key}`}
                      className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
                      <option value={0}>—</option>
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <textarea value={scores.comment} onChange={(e) => setScores({ ...scores, comment: e.target.value })}
                placeholder="Comments (private to your team)" rows={2} data-testid="ivroom-score-comment"
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" />
              <button disabled={saving === "score"} data-testid="ivroom-score-save"
                className="h-9 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                {saving === "score" ? "Saving…" : "Save scorecard"}
              </button>
            </form>
            {iv.scorecards?.length > 0 && (
              <div className="mt-4 space-y-2">
                {iv.scorecards.map((sc) => (
                  <div key={sc.email} className="text-sm text-zinc-600 border-t border-zinc-100 pt-2" data-testid={`ivroom-scored-${sc.email}`}>
                    <span className="font-medium text-zinc-800">{sc.name || sc.email}</span> · overall {sc.axes?.overall || "—"}/5
                    {sc.comment ? <span className="text-zinc-500"> · {sc.comment}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-8 mt-6" data-testid="ivroom-notes">
            <h2 className="font-display text-lg font-bold text-zinc-950 mb-3">Team notes</h2>
            {iv.notes?.length > 0 && (
              <ul className="space-y-2 mb-4">
                {iv.notes.map((n, i) => (
                  <li key={`${n.at}-${n.author || n.author_name}`} className="text-sm text-zinc-600">
                    <span className="font-medium text-zinc-800">{n.author_name || n.author}</span> · {new Date(n.at).toLocaleString("en-GB")}<br />{n.text}
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={addNote} className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (private to your team)"
                data-testid="ivroom-note-input" className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-sm" />
              <button disabled={saving === "note"} data-testid="ivroom-note-save"
                className="h-10 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">Add</button>
            </form>
          </div>

          {iv.status === "scheduled" && new Date(iv.at) < new Date() && (
            <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-8 mt-6" data-testid="ivroom-attendance">
              <h2 className="font-display text-lg font-bold text-zinc-950 mb-3">Attendance</h2>
              <div className="flex gap-2">
                <button onClick={() => mark(true)} data-testid="ivroom-attended"
                  className="h-10 px-5 bg-green-600 text-white text-sm font-semibold rounded-md hover:bg-green-700 transition-colors">Attended</button>
                <button onClick={() => mark(false)} data-testid="ivroom-noshow"
                  className="h-10 px-5 border border-zinc-300 text-sm font-semibold rounded-md hover:border-red-400 hover:text-red-600 transition-colors">No-show</button>
              </div>
            </div>
          )}

          {iv.history?.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg p-6 sm:p-8 mt-6" data-testid="ivroom-history">
              <h2 className="font-display text-lg font-bold text-zinc-950 mb-3">History</h2>
              <ul className="space-y-1.5">
                {iv.history.map((h, i) => (
                  <li key={`${h.at}-${h.action}`} className="text-xs text-zinc-500">
                    {new Date(h.at).toLocaleString("en-GB")} · {h.by} · {h.action.replace(/_/g, " ")}{h.detail ? `, ${h.detail}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </main>
  );
}
